import { Box, Heading, Link, Text } from "@chakra-ui/react";
import React from "react";
import { useMachine } from "@xstate/react";
import { signupMachine } from "./machine";
import MemberDetailsForm from "./screens/memberDetails";
import PlansForm from "./screens/plans";
import PaymentForm from "./screens/payment";
import { PaymentMethod } from "@stripe/stripe-js";
import { ApplicationType, SignupFormData } from "./types";
import { PageWithSideBar } from "../layout/PageWithSideBar";
import ReivList from "../common/reivList";
import SuccessPage from "./screens/success";
import Stripe from "stripe";

type PaymentSubmitFunction = (
  paymentMethod: PaymentMethod,
  invoiceId: string | undefined,
  customerId: string | undefined,
  priceLookUpKey: string,
  memberDetails: SignupFormData
) => Promise<{
  subscription: Stripe.Response<Stripe.Subscription>;
  priceLookUpKey: string;
  paymentMethodId: string;
  invoice: Stripe.Response<Stripe.Invoice> | undefined;
}>;

const SignupForm: React.FC<{ handlePaymentSubmit: PaymentSubmitFunction }> = ({
  handlePaymentSubmit,
}) => {
  const [current, send] = useMachine(signupMachine, {
    context: {},
  });

  if (current.matches("memberDetails")) {
    return (
      <PageWithSideBar
        SideBarHeading={
          <Text fontSize="3xl" textAlign="center">
            Joining the REIV puts you on the path to being the best you can be
            in Victorian property.
          </Text>
        }
        SideBarContent={
          <ReivList
            data={[
              { description: "Join the industry’s best and brightest" },
              {
                description:
                  "Contribute to the growth and direction of the industry",
              },
              { description: "Get the latest property data" },
              { description: "Keep your skills honed" },
              { description: "Advance your career" },
            ]}
          />
        }
        SideBarDisclaimer={
          <Text fontSize="sm" padding="0 4px 0 4px">
            <Heading size="sm">Who this form is for</Heading>
            This application form is for agents who hold an Estate Agent's
            License or who work as Agent’s Representatives. You must be
            registered with the Business Licensing Authority under the agency
            listed on your application before you can be formally approved.{" "}
            <br /> <br /> If you are a Valuer or Owners Corporation Manager,
            please complete and send this application form instead.
          </Text>
        }
        PageHeader={
          <>
            <Heading size="medium" textAlign="center">
              2021/2022 REIV Individual Membership Application
            </Heading>
            <Text textAlign="center">
              (Licensed Members and Representative Members)
            </Text>
          </>
        }
      >
        <MemberDetailsForm
          initialValues={current.context.memberDetails}
          handleSubmit={(values) => {
            send({
              type: "PROCEED",
              values: values,
            } as any);
          }}
        />
      </PageWithSideBar>
    );
  }

  if (current.matches("plans")) {
    return (
      <PageWithSideBar
        SideBarHeading={
          <Text fontSize="2xl" textAlign="center">
            Flexible membership payments are just the beginning
          </Text>
        }
        SideBarContent={
          <ReivList
            data={[
              { description: "Learn and upskill" },
              { description: "Grow your network and build your brand" },
              { description: "Access credible data and insights" },
              { description: "Take advantage of amazing member rewards" },
            ]}
          />
        }
        SideBarDisclaimer={
          <Text fontSize="sm" padding="0 4px 0 4px">
            Your salary shouldn’t limit you from getting access to the great
            insights and support that an REIV membership offers. That’s why you
            can choose to pay your individual membership fee monthly* or
            annually.
            <br />
            <br />
            *Monthly memberships must be paid for 12 consecutive months to
            ensure full access to member benefits.
          </Text>
        }
        PageHeader={
          <Heading size="medium" textAlign="center">
            Choose your preferred membership payment
          </Heading>
        }
      >
        <PlansForm
          selectedPrice={current.context.priceLookUpKey}
          handleSelection={(values) => {
            send({
              type: "UPDATE",
              values: values,
            });
          }}
          backButtonHandler={() => send("BACK")}
          proceedButtonHandler={() => send("PROCEED")}
          applicationType={
            current.context?.memberDetails?.applicationType as ApplicationType
          }
        />
      </PageWithSideBar>
    );
  }

  if (current.matches("payment")) {
    return (
      <PageWithSideBar
        SideBarHeading={
          <Text fontSize="2xl" textAlign="center">
            You’re a click away from becoming an REIV individual member*
          </Text>
        }
        SideBarContent={
          <ReivList
            data={[
              { description: "Learn and upskill" },
              { description: "Grow your network and build your brand" },
              { description: "Access credible data and insights" },
              { description: "Take advantage of amazing member rewards" },
            ]}
            mt={"60px!important"}
          />
        }
        SideBarDisclaimer={
          <Text fontSize="sm" padding="0 4px 0 4px">
            If you don’t qualify as an individual member (that is, you aren’t a
            Licensed Estate Agent, Agent’s Representative, Owners Corporation
            Manager or Valuer) we’ll let you know and you can either cancel your
            application and we’ll refund your application fee minus a small
            processing fee. Otherwise we can help you{" "}
            <Link href="https://reiv.com.au/membership/affiliate-of-the-institute">
              apply as an Affiliate Member.
            </Link>
          </Text>
        }
        PageHeader={
          <Heading size="medium" textAlign="center">
            Provide your payment details
          </Heading>
        }
      >
        <PaymentForm
          processPayment={async (paymentMethod, invoiceId, customerId) => {
            const paymentResponse = await handlePaymentSubmit(
              paymentMethod,
              invoiceId,
              customerId,
              current.context.priceLookUpKey,
              current.context.memberDetails
            );
            send({
              type: "PROCEED",
              values: paymentResponse.subscription
                .latest_invoice as Stripe.Invoice,
            });
          }}
          onBack={() => {
            send("BACK");
          }}
        />
      </PageWithSideBar>
    );
  }

  if (current.matches("success")) {
    return (
      <Box minH="100vh" paddingTop="1rem">
        <SuccessPage invoice={current.context.invoice} />
      </Box>
    );
  }

  return <Box>Signup</Box>;
};

export default SignupForm;
